import React, { useEffect, useState } from "react";
import { Spinner, Pagination, Button, TextInput } from "flowbite-react";
import { HiSearch, HiCheckCircle, HiXCircle, HiClock } from "react-icons/hi";
import axios from "../api/axios";
import { useAuth } from "../../src/context/AuthContext";

const PAGE_SIZE = 5;

const statusColors = {
  Paid: "text-green-600 bg-green-500/10",
  Failed: "text-red-600 bg-red-500/10",
  Created: "text-yellow-600 bg-yellow-500/10",
};

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({ paid: 0, failed: 0, created: 0 });

  const { getToken } = useAuth();

  const fetchTransactions = async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await axios.get(
        `https://api.foodoscope.com/admin/transactions?page=${pageNum}&pageSize=${PAGE_SIZE}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTransactions(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError("Failed to fetch transactions.");
    }
    setLoading(false);
  };

  const fetchTransactionStats = async () => {
    try {
      const token = await getToken();
      // Fetch all transactions to calculate accurate stats
      const res = await axios.get(
        `https://api.foodoscope.com/admin/transactions?page=1&pageSize=1000`, // Adjust size as needed
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const allTransactions = res.data.data || [];
      const statsData = {
        paid: allTransactions.filter(t => t.status === "Paid").length,
        failed: allTransactions.filter(t => t.status === "Failed").length,
        created: allTransactions.filter(t => t.status === "Created").length,
      };
      setStats(statsData);
    } catch (err) {
      console.error("Failed to fetch transaction stats:", err);
    }
  };

  useEffect(() => {
    fetchTransactions(page);
  }, [page]);

  useEffect(() => {
    // Fetch stats only once when component mounts
    fetchTransactionStats();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(transactions);
    } else {
      setFiltered(
        transactions.filter(
          (t) =>
            t.email.toLowerCase().includes(search.toLowerCase()) ||
            t.transactionId.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [transactions, search]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const statusIcon = (status) => {
    if (status === "Paid")
      return <HiCheckCircle className="w-4 h-4 text-green-600 inline mr-1" />;
    if (status === "Failed")
      return <HiXCircle className="w-4 h-4 text-red-600 inline mr-1" />;
    return <HiClock className="w-4 h-4 text-yellow-600 inline mr-1" />;
  };

  return (
    <div className="h-full bg-[#232323] rounded-2xl p-4 sm:p-6 lg:p-8 flex flex-col border border-[#2A2A2A] shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">Transactions</h2>
          <p className="text-[#8C9086] text-sm font-medium">Monitor all user payment transactions</p>
        </div>
        <div className="relative w-full sm:w-72">
          <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Email and TransactionID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-72 pl-9 pr-4 py-2 text-sm rounded-lg bg-[#232323] border border-gray-600 text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-[#B8D449] focus:border-[#B8D449] transition-all"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {/* Total Transactions */}
        <div className="bg-[#2A2B2D] rounded-2xl p-4 sm:p-5 border border-white/5 hover:border-white/10 transition-colors shadow-sm">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <span className="text-blue-500 text-sm font-bold">T</span>
            </div>
            <div>
              <p className="text-[#8C9086] text-xs uppercase tracking-wide font-bold mb-0.5">Total</p>
              <p className="text-white text-xl font-bold leading-none">{total}</p>
            </div>
          </div>
        </div>

        {/* Paid */}
        <div className="bg-[#2A2B2D] rounded-2xl p-4 sm:p-5 border border-white/5 hover:border-white/10 transition-colors shadow-sm">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <span className="text-green-500 text-sm font-bold">P</span>
            </div>
            <div>
              <p className="text-[#8C9086] text-xs uppercase tracking-wide font-bold mb-0.5">Paid</p>
              <p className="text-white text-xl font-bold leading-none">{stats.paid}</p>
            </div>
          </div>
        </div>

        {/* Failed */}
        <div className="bg-[#2A2B2D] rounded-2xl p-4 sm:p-5 border border-white/5 hover:border-white/10 transition-colors shadow-sm">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <span className="text-red-500 text-sm font-bold">F</span>
            </div>
            <div>
              <p className="text-[#8C9086] text-xs uppercase tracking-wide font-bold mb-0.5">Failed</p>
              <p className="text-white text-xl font-bold leading-none">{stats.failed}</p>
            </div>
          </div>
        </div>

        {/* Created */}
        <div className="bg-[#2A2B2D] rounded-2xl p-4 sm:p-5 border border-white/5 hover:border-white/10 transition-colors shadow-sm">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-yellow-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <span className="text-yellow-500 text-sm font-bold">C</span>
            </div>
            <div>
              <p className="text-[#8C9086] text-xs uppercase tracking-wide font-bold mb-0.5">Created</p>
              <p className="text-white text-xl font-bold leading-none">{stats.created}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden border border-[#c5c2b8]" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.10)' }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-[#B8B5AA]">
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-[#4a4a4a] uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                  Transaction ID
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">
                  Email
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.04]">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <Spinner size="xl" color="info" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <p className="text-red-500 font-medium mb-4">{error}</p>
                    <button 
                      onClick={() => fetchTransactions(page)}
                      className="inline-flex items-center px-4 py-2 text-sm font-bold rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="w-16 h-16 bg-[#D5DABF]/30 rounded-full flex items-center justify-center text-[#8C9086]">
                        <HiSearch className="w-8 h-8" />
                      </div>
                      <p className="text-[#8C9086] font-medium text-sm">
                        {search
                          ? `No transactions found matching "${search}"`
                          : "No transactions available"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr 
                    key={t.transactionId} 
                    className="hover:bg-[#DEE3D0] transition-colors duration-200 group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-[#161616] font-semibold text-sm">{new Date(t.date).toLocaleDateString()}</span>
                        <span className="text-[#8C9086] text-xs font-medium">{new Date(t.date).toLocaleTimeString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-[#3A3A3A] text-sm font-bold">
                        {t.transactionId}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[#3A3A3A] text-sm font-medium">{t.description}</td>
                    <td className="px-6 py-4 text-center whitespace-nowrap text-[#161616] font-bold text-sm">
                      {t.amount}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center space-x-1.5">
                        <div className={`w-2 h-2 rounded-full ${t.status === 'Paid' ? 'bg-green-500' : t.status === 'Failed' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                        <span className="text-[#3A3A3A] text-sm font-semibold">{t.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-[#3A3A3A] text-sm font-semibold">{t.email}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-[#D5DABF] bg-[#E8ECDA] flex items-center justify-center sm:justify-between flex-wrap gap-4 mt-auto rounded-b-3xl">
            <span className="text-sm text-[#8C9086] font-medium hidden sm:block">
              Page <span className="text-[#3A3A3A] font-bold">{page}</span> of <span className="text-[#3A3A3A] font-bold">{totalPages}</span>
            </span>
            
            <div className="flex space-x-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-semibold text-[#8C9086] rounded-xl hover:bg-[#D5DABF] disabled:opacity-50 disabled:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C9086]/50"
              >
                &larr; Previous
              </button>
              
              {/* Desktop Page Numbers */}
              <div className="hidden sm:flex space-x-1 px-2">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-9 h-9 rounded-xl text-sm font-bold flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                      pageNum === page
                        ? 'bg-[#3B82F6] text-white shadow-sm'
                        : 'text-[#8C9086] hover:bg-[#D5DABF] hover:text-[#3A3A3A]'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm font-semibold text-[#8C9086] rounded-xl hover:bg-[#D5DABF] disabled:opacity-50 disabled:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#8C9086]/50"
              >
                Next &rarr;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTransactions;